import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Card, CardContent, Typography, Box, Chip, IconButton, useTheme, Radio, Divider, ListItemAvatar, FormControl, Autocomplete, TextField, Paper } from '@mui/material';
import { useState } from 'react';
import { font } from '../../../styles/font';
import { CloseOutlined, Memory, PeopleOutline } from '@mui/icons-material';
import TemplateLoaderComponent from '../../loader/TemplateLoaderComponent';
import { useApplicationContext } from '../../../context/ApplicationContext';
import { getLicenseAddOnList_API, getLicenseList_API, subscribeLicensePlan_API } from '../../../api/controller/LicenseController';
import { HttpStatusCode } from 'axios';
import { Severity, USER_ROLE } from '../../../constant/ApplicationVariables';
import { useEffect } from 'react';
import useErrorHandler from '../../../api/errorHandler/useErrorHandler';
import { trialPayment_API } from '../../../api/controller/PaymentsController';

const LICENSE_PACKAGES = [
  {
    name: 'Starter',
    price: 49,
    users: 5,
    description: 'Perfect for small teams getting started with professional tools. Includes core functionality access, basic reporting, email support, and standard integrations to help your team work efficiently.',
    mostPopular: false,
  },
  {
    name: 'Professional',
    price: 149,
    users: 20,
    description: 'Advanced features for growing businesses and teams. Everything in Starter plus advanced analytics, priority support, custom integrations, and enhanced security features for professional workflows.',
    mostPopular: true,
  },
  {
    name: 'Enterprise',
    price: 399,
    users: 100,
    description: 'Complete solution for large organizations. All Professional features plus unlimited customization, dedicated support, SLA guarantee, advanced compliance tools, and custom training programs.',
    mostPopular: false,
  },
];

const ADDONS = [
  { name: 'Additional Users', price: 8, priceType: 'Per Unit' },
  { name: 'Premium Support', price: 99, priceType: 'Fixed Price' },
  { name: 'Advanced Analytics', price: 49, priceType: 'Fixed Price' },
  { name: 'Custom Integrations', price: 199, priceType: 'Per Unit' },
];

export default function ChooseLicenseDialog({ data, handleClose, handleConfirm }) {
  const { handleSnackbar, userDetail } = useApplicationContext();
  const theme = useTheme();
  const errorHandler = useErrorHandler();

  const [loading, setLoading] = useState(false);
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedAddonWithUnitList, setSelectedAddonWithUnitList] = useState([]);

  const [licenseList, setLicenseList] = useState([]);
  const [licenseAddOnList, setLicenseAddOnList] = useState([]);

  let subTotalCount = selectedAddonWithUnitList?.reduce((total, addon) => total + addon.unitPrice * addon.units, 0) + selectedPackage?.basePrice;
  let totalCount = subTotalCount

  // Determine button text color based on theme mode
  const getButtonTextColor = () => {
    return theme.palette.mode === 'light' ? '#ffffff' : '#000000';
  }

  const handlePackageSelect = (data) => {
    setSelectedPackage(data);
  }

  const handleAddonToggle = (data) => {
    let foundData = selectedAddons.find((a) => a.name === data.name);

    setSelectedAddons((prev) =>
      foundData ? prev.filter((a) => a?.name !== data?.name) : [...prev, data]
    );
  };

  // Function to handle add units count per add 
  const handleChangeAddonUnit = (e, index) => {
    let name = e.target.name;
    let value = e.target.value;

    let unitAddonList = [...selectedAddonWithUnitList];
    unitAddonList[index].units = Number(value);
    setSelectedAddonWithUnitList(unitAddonList);
  }

  // Handle change autocomplete options click
  const handleChangeAutocomplete = (event, newValue) => {
    let unitAddonList = [...selectedAddonWithUnitList];
    newValue?.forEach((item) => {
      let foundData = unitAddonList.find((a) => a.code === item.code);
      if (!foundData) {
        unitAddonList.push({ ...item, units: 1 });
      }
    })

    setSelectedAddonWithUnitList(unitAddonList);
    setSelectedAddons(newValue);
  };

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleRemoveAddon = (item) => {
    setSelectedAddonWithUnitList((prev) => prev.filter((a) => a?.code !== item?.code));
    setSelectedAddons((prev) => prev.filter((a) => a?.code !== item?.code));
  }

  // Function to handle button click trial / purchase
  const handlePurchase = (isTrial) => {
    // For Non Super Admin User 
    if (!isSuperAdminUser) {
      handleSubscriptionPurchase();
    }

    // For Super Admin User
    if (isSuperAdminUser) {
      if (Array.isArray(data) && data.length > 0) {
        data?.forEach((item) => {
          if (isTrial) {
            handleTrialPurchase(item)
          } else {
            handleSubscriptionPurchase(item);
          }
        });
      } else {
        if (isTrial) {
          handleTrialPurchase(data)
        } else {
          handleSubscriptionPurchase(data);
        }
      }
    }
  }

  const handleGetLicensePackageList = async () => {
    setLoading(true);
    try {
      let query = `active=false`
      const response = await getLicenseList_API(query);
      if (response) {
        errorHandler(response, setLoading);
      }
      if (response.status === HttpStatusCode.Ok) {
        setLicenseList(response.data?.filter(addon => addon.active));
        setSelectedPackage(response.data?.filter(addon => addon.active)[0])
      }
    } catch (error) {
      console.log(error, error?.message, "projectreponse errorr");
      handleSnackbar(String(error), Severity.error);
    } finally {
      setLoading(false);
    }
  }

  const handleGetAddOnList = async () => {
    setLoading(true);
    try {
      let query = `active=false`
      const response = await getLicenseAddOnList_API(query);
      if (response) {
        errorHandler(response, setLoading);
      }
      if (response.status === HttpStatusCode.Ok) {
        setLicenseAddOnList(response.data?.filter(addon => addon.active));
      }
    } catch (error) {
      console.log(error, error?.message, "projectreponse errorr");
      handleSnackbar(String(error), Severity.error);
    } finally {
      setLoading(false);
    }
  }

  const handleTrialPurchase = async (item) => {
    setLoading(true);
    try {
      let body = {
        companyId: item?.id,
        code: selectedPackage?.code,
        addOns: [...selectedAddonWithUnitList?.map((a) => ({ code: a.code, quantity: a.units }))]
      }

      const response = await trialPayment_API(body);
      if (response) {
        errorHandler(response, setLoading);
      }
      if (response.status === HttpStatusCode.Ok) {
        handleSnackbar(response?.data?.message || "Trial assigned Successfully", Severity.success);
        handleConfirm();
      }
    } catch (error) {
      handleSnackbar(String(error), Severity.error);
    } finally {
      setLoading(false);
    }
  }

  const handleSubscriptionPurchase = async () => {
    setLoading(true);
    try {
      let body = {
        code: selectedPackage?.code,
        addOns: [...selectedAddonWithUnitList?.map((a) => ({ code: a.code, quantity: a.units }))]
      }

      const response = await subscribeLicensePlan_API(body);
      if (response) {
        errorHandler(response, setLoading);
      }
      if (response.status === HttpStatusCode.Ok) {
        handleSnackbar(response?.data?.message || "License activation request submitted!", Severity.success);
        handleConfirm();
      }
    } catch (error) {
      handleSnackbar(String(error), Severity.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    handleGetLicensePackageList();
    handleGetAddOnList();
  }, []);

  // to update state based on user role
  useEffect(() => {
    if ((userDetail?.role)?.toLowerCase() === USER_ROLE.superadmin?.toLowerCase()) {
      setIsSuperAdminUser(true);
    } else {
      setIsSuperAdminUser(false);
    }
  }, [userDetail?.role]);

  return (
    <Box className="flex-1 flex flex-col gap-4 px-4 py-4 relative overflow-hidden">
      {/* header  */}
      <Box sx={{ display: "flex", alignItems: "center", }}>
        <Box className="flex-1 lex flex-col">
          <Typography 
            sx={{ 
              fontWeight: 700, 
              fontSize: font.size.fs_3,
              color: theme.palette.primary.main  // Primary color for header
            }}
          >
            Choose Your License Package
          </Typography>
          <Typography color={theme.palette.text.secondary}>
            Select the perfect plan for your team and enhance it with powerful add-ons
          </Typography>
        </Box>

        {handleClose && (
          <IconButton onClick={handleClose}>
            <CloseOutlined />
          </IconButton>
        )}
      </Box>

      {/* main container */}
      <Box className="w-full h-[80vh] flex gap-2 relative overflow-hidden overflow-y-auto">
        {loading && <TemplateLoaderComponent />}

        {/* main container */}
        <Box className="flex-1 flex items-center gap-0">

          {/* license / addon */}
          <Box className="grow h-full flex flex-col gap-4 overflow-y-auto">

            {/* license package container */}
            <Box className="h-fit flex flex-col gap-2">
              <Box className="flex items-center w-full px-0 py-0">
                <Typography sx={{ fontSize: font.size.fs_4, fontWeight: 600, color: theme.palette.text.primary }}>
                  License Packages
                </Typography>
              </Box>

              <Box className="w-full h-fit flex items-center gap-4 overflow-x-auto">
                {loading ? (
                  <Typography>Loading...</Typography>
                ) :
                  licenseList?.filter(addon => addon.active)?.map((pkg, index) => (
                    <Box key={pkg.name}>
                      <Card
                        variant={selectedPackage?.name === pkg?.name ? 'outlined' : 'elevation'}
                        sx={{
                          borderColor: selectedPackage?.name === pkg?.name ? theme.palette.warning.main : theme.palette.divider,
                          borderWidth: 2,
                          cursor: 'pointer',
                          boxShadow: selectedPackage?.name === pkg?.name ? 4 : 1,
                          position: 'relative',
                          height: 200,
                          width: 240,
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handlePackageSelect(pkg)}
                      >
                        <CardContent className="flex flex-col gap-2">
                          <Box className="flex flex-col gap-1">
                            <Typography sx={{ fontSize: font.size.fs_4, fontWeight: 700, color: theme.palette.text.primary }}>
                              {pkg?.name}
                            </Typography>

                            <Typography sx={{ fontSize: font.size.fs_2, fontWeight: 700, color: theme.palette.warning.main }}>
                              ${pkg?.basePrice}<Typography component="span" sx={{ fontWeight: 400, color: theme.palette.text.secondary }}>/{pkg?.planBilling}</Typography>
                            </Typography>
                          </Box>
                          <Typography color={theme.palette.text.secondary}>
                            <PeopleOutline />  {pkg?.includedUsers} users included
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{pkg?.description}</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
              </Box>
            </Box>

            {/* addons container */}
            <Box className="flex-1 flex flex-col gap-2 overflow-hidden">
              <Box className="flex items-center w-full h-fit px-0 py-0">
                <Typography sx={{ fontSize: font.size.fs_4, fontWeight: 600, color: theme.palette.text.primary }}>
                  Available Add-ons {selectedAddonWithUnitList.length > 0 ? `- (${selectedAddonWithUnitList.length})` : null}
                </Typography>
              </Box>

              <FormControl>
                <Autocomplete
                  multiple
                  id="addons-autocomplete"
                  options={licenseAddOnList?.filter(addon => addon.active)}
                  getOptionLabel={(option) => option.name + " - $" + option.unitPrice}
                  value={selectedAddons}
                  onChange={handleChangeAutocomplete}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search Add-on using name"
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                  renderTags={() => null}
                  filterSelectedOptions
                  isOptionEqualToValue={(option, value) => option.name === value.name}
                  className="w-72"
                />
              </FormControl>

              <Box className="flex-1 flex gap-4 overflow-x-hidden overflow-y-auto">
                <Box className="flex-1 flex flex-col gap-2 overflow-y-auto">
                  {loading ? (
                    <Box className="flex-1 w-full flex flex-col items-center justify-center">
                      <Typography>loading...</Typography>
                    </Box>
                  ) : selectedAddonWithUnitList?.length === 0 ? (
                    <Box className="flex-1 w-full flex flex-col items-center justify-center">
                      <Typography>No Add-on selected</Typography>
                    </Box>
                  ) : selectedAddonWithUnitList?.map((addon, index) => (
                    <Paper
                      key={addon.name}
                      variant={'outlined'}
                    >
                      <Box className="flex items-center gap-2 px-4 py-2">
                        <Box>
                          <Memory sx={{ color: theme.palette.warning.main, fontSize: font.size.fs_2 }} />
                        </Box>

                        <Typography className="flex-1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {addon.name}
                        </Typography>

                        <Box className="flex items-center gap-2 w-44">
                          <TextField 
                            type="number" 
                            className="w-16" 
                            disabled={!addon.chargedPerUnit} 
                            value={addon?.units || 1} 
                            onChange={e => handleChangeAddonUnit(e, index)} 
                          />  
                          <Typography sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                            x {addon.unitPrice} {addon.chargedPerUnit ? `/ per unit` : `/ fixed price`}
                          </Typography>
                        </Box>

                        <IconButton 
                          color="error" 
                          onClick={() => handleRemoveAddon(addon)}
                          sx={{
                            color: theme.palette.warning.main,
                            '&:hover': {
                              backgroundColor: theme.palette.warning.main,
                              color: getButtonTextColor(),
                            }
                          }}
                        >
                          <CloseOutlined />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* total / sub total */}
          <Box className="w-[40%] h-full flex flex-col px-4 py-0 gap-2 overflow-y-auto">
            <Paper variant="outlined" sx={{ backgroundColor: theme.palette.highlight.light }} className="flex-1 flex flex-col gap-4 px-4 py-4">
              <Typography fontSize={font.size.fs_3} fontWeight={"bold"} color={theme.palette.text.primary}>
                License Package
              </Typography>

              <KeyValue count={selectedPackage?.basePrice}>
                <Box className="flex-1 flex flex-col">
                  <Typography fontSize={font.size.fs_4} fontWeight={"bold"} color={theme.palette.text.primary}>
                    {selectedPackage?.name}
                  </Typography>
                  <Typography color={theme.palette.text.secondary}>{selectedPackage?.planBilling}</Typography>
                </Box>
              </KeyValue>

              {selectedAddonWithUnitList?.length > 0 && <Divider />}

              {selectedAddonWithUnitList?.length > 0 && (
                <Box className="flex flex-col gap-2">
                  <Typography fontSize={font.size.fs_4} fontWeight={"bold"} color={theme.palette.text.primary}>
                    Add-on
                  </Typography>
                  <Box className="flex flex-col gap-2">
                    {selectedAddonWithUnitList?.map((addon, index) => (
                      <KeyValue key={addon.name} count={addon.unitPrice * addon.units}>
                        <Box className="flex-1 flex flex-col">
                          <Typography fontWeight={600} color={theme.palette.text.primary}>
                            {addon.name}
                          </Typography>
                          <Typography color={theme.palette.text.secondary}>
                            {`${addon.units} × $${addon.unitPrice}`}
                          </Typography>
                        </Box>
                      </KeyValue>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider />

              <KeyValue count={subTotalCount}>
                <Box className="flex-1 flex flex-col">
                  <Typography fontSize={font.size.fs_4} fontWeight={"bold"} color={theme.palette.warning.main}>
                    Sub Total
                  </Typography>
                </Box>
              </KeyValue>

              <Divider />

              <KeyValue count={totalCount}>
                <Box className="flex-1 flex flex-col">
                  <Typography fontSize={font.size.fs_4} fontWeight={"bold"} color={theme.palette.warning.main}>
                    Total
                  </Typography>
                </Box>
              </KeyValue>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* footer */}
      <Box className="flex items-center justify-end gap-4">
        {isSuperAdminUser ? (
          <Button 
            disabled={loading} 
            onClick={() => handlePurchase(true)}
            sx={{
              backgroundColor: theme.palette.warning.main,
              color: getButtonTextColor(),
              '&:hover': {
                backgroundColor: theme.palette.warning.dark,
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              }
            }}
          >
            Assign Trial
          </Button>
        ) : (
          <Button 
            disabled={loading} 
            onClick={() => handlePurchase(false)}
            sx={{
              backgroundColor: theme.palette.warning.main,
              color: getButtonTextColor(),
              '&:hover': {
                backgroundColor: theme.palette.warning.dark,
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              }
            }}
          >
            Purchase Now
          </Button>
        )}
      </Box>
    </Box>
  );
}

function KeyValue({ children, count }) {
  const theme = useTheme();
  
  return (
    <Box className="flex items-center gap-4">
      <Box className="flex-1 flex flex-col">
        {children}
      </Box>
      {typeof count === "string" || typeof count === "number" ? (
        <Typography fontSize={font.size.fs_5} fontWeight={"bold"} color={theme.palette.warning.main}>
          ${count}
        </Typography>
      ) : count}
    </Box>
  );
}